import React, { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSpreadsheetStore } from './store/spreadsheetStore';
import SpreadsheetToolbar from './SpreadsheetToolbar';
import SpreadsheetGrid from './SpreadsheetGrid';
import SheetTabs from './SheetTabs';
import FormulaBar from './FormulaBar';
import ContextMenu from './ContextMenu';
import { useSelector } from 'react-redux';
import axios from 'axios';

import { message } from 'antd';
import './SpreadsheetBuilder.css';

const SpreadsheetBuilder = ({ readOnly = false, initialData = null }) => {
  const { formId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = location.pathname.includes('spreadsheet-edit');
  const isReadOnlyMode = readOnly || location.pathname.includes('/spreadsheet/') && !location.pathname.includes('edit');
  
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);
  
  const {
    activeSheetIndex,
    sheets,
    setActiveCell,
    copySelection,
    pasteSelection,
    setEditing,
    isEditing,
    getActiveSheet,
    loadSpreadsheetFromAPI,
    tableTitle,
    tableDescription,
    setTableTitle,
    setTableDescription,
    setSheets,
    isLoading
  } = useSpreadsheetStore();

  const gridRef = useRef(null);
  const activeSheet = getActiveSheet();

  // FormBuilder benzeri kaydetme fonksiyonu - OPTIMIZE
  const handleSaveTable = async (title, description = '', folderIds = ['0']) => {
    try {
      const newFormId = formId || Math.floor(100000 + Math.random() * 900000).toString();
      
      console.log('🔥 SpreadsheetBuilder - Kaydetme işlemi başlıyor:', { formId, newFormId, title, folderIds });
      console.log('🔥 SpreadsheetBuilder - Gelen parametreler:', { title, description, folderIds });
      console.log('🔥 SpreadsheetBuilder - FolderIds type:', typeof folderIds, Array.isArray(folderIds));
      
      // Sheets'i optimize et - sadece kullanılan hücreleri kaydet
      const optimizedSheets = sheets.map((sheet, sheetIndex) => {
        console.log(`Sheet ${sheetIndex} optimize ediliyor...`);
        
        // Kullanılan hücreleri belirle
        let maxUsedRow = 0;
        let maxUsedCol = 0;
        const usedCells = [];
        
        for (let rowIndex = 0; rowIndex < sheet.grid.length; rowIndex++) {
          const row = sheet.grid[rowIndex];
          if (Array.isArray(row)) {
            for (let colIndex = 0; colIndex < row.length; colIndex++) {
              const cell = row[colIndex];
              if (cell && (cell.value || cell.formula)) {
                maxUsedRow = Math.max(maxUsedRow, rowIndex);
                maxUsedCol = Math.max(maxUsedCol, colIndex);
                usedCells.push({ row: rowIndex, col: colIndex, cell });
              }
            }
          }
        }
        
        // Minimum grid boyutu belirle
        const minRows = Math.max(maxUsedRow + 10, 50);
        const minCols = Math.max(maxUsedCol + 10, 26);
        
        // Optimize edilmiş grid oluştur
        const optimizedGrid = [];
        for (let rowIndex = 0; rowIndex < minRows; rowIndex++) {
          const row = [];
          for (let colIndex = 0; colIndex < minCols; colIndex++) {
            if (sheet.grid[rowIndex] && sheet.grid[rowIndex][colIndex]) {
              row.push(sheet.grid[rowIndex][colIndex]);
            } else {
              row.push({
                value: '',
                formula: '',
                format: {
                  bold: false,
                  italic: false,
                  underline: false,
                  fontSize: 11,
                  fontFamily: 'Arial',
                  textColor: '#000000',
                  backgroundColor: '#ffffff',
                  horizontalAlign: 'left',
                  verticalAlign: 'top',
                  border: {
                    top: { style: 'none', color: '#000000' },
                    right: { style: 'none', color: '#000000' },
                    bottom: { style: 'none', color: '#000000' },
                    left: { style: 'none', color: '#000000' }
                  }
                }
              });
            }
          }
          optimizedGrid.push(row);
        }
        
        console.log(`Sheet ${sheetIndex} optimize edildi: ${minRows}x${minCols}, kullanılan hücreler: ${usedCells.length}`);
        
        return {
          ...sheet,
          grid: optimizedGrid
        };
      });
      
      // Tablo içeriği objesi (FormBuilder benzeri)
      const formContentObj = {
        id: newFormId,
        title: title,
        description: description,
        sheets: optimizedSheets,
        settings: {
          theme: 'default',
          gridLines: true,
          headers: true,
          autoSave: true
        },
        isPublished: true,
        publishedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        formLink: `https://panel.formclicker.com/user-login?tableId=${newFormId}`
      };

      console.log('FormContent objesi hazırlandı, sheets sayısı:', formContentObj.sheets.length);

      // FolderIds array'ini işle - tek string, array veya string olarak birleştir
      let parentIdValue;
      if (Array.isArray(folderIds)) {
        if (folderIds.length === 1) {
          parentIdValue = folderIds[0].toString();
        } else if (folderIds.length > 1) {
          // Çoklu klasör: API'nin nasıl kabul ettiğine göre ayarlayın
          // Seçenek 1: Array olarak gönder (API array destekliyorsa)
          // parentIdValue = folderIds;
          // Seçenek 2: Virgülle ayrılmış string olarak gönder (API string destekliyorsa)
          parentIdValue = folderIds.join(',');
        } else {
          parentIdValue = '0';
        }
      } else {
        parentIdValue = folderIds ? folderIds.toString() : '0';
      }
      
      console.log('🔥 SpreadsheetBuilder - ProcessedParentId:', parentIdValue);
      console.log('🔥 SpreadsheetBuilder - ProcessedParentId type:', typeof parentIdValue);
      
      // API'ye gönderilecek içerik (FormBuilder benzeri)
      const content = {
        parentId: parentIdValue, // Array veya string olarak gönder
        formId: newFormId,
        title: title,
        description: description,
        formContent: formContentObj, // OBJECT olarak gönder, JSON.stringify yapma!
        isActive: true,
        fileType: 1 // Tablo tipi için fileType = 1
      };

      console.log('🔥 SpreadsheetBuilder - Kaydedilecek tablo hazırlandı:');
      console.log('🔥 SpreadsheetBuilder - content.parentId:', content.parentId);
      console.log('🔥 SpreadsheetBuilder - content.parentId type:', typeof content.parentId);
      console.log('🔥 SpreadsheetBuilder - content.fileType:', content.fileType);
      console.log('- formContent tipi:', typeof content.formContent);
      console.log('- formContent keys:', Object.keys(content.formContent));
      console.log('- sheets sayısı:', content.formContent.sheets?.length);
      console.log('- Content object preview:', {
        ...content,
        formContent: '[OBJECT - ' + Object.keys(content.formContent).length + ' keys]'
      });

      let response;
      if (formId) {
        // Güncelleme
        content.id = parseInt(formId);
        console.log('Tablo güncelleniyor:', formId);
        response = await axios.put(`https://app.hhoms.com/api/form/Libraries/${formId}`, content, {
          headers: {
            ApplicationToken: appToken,
            User: userData.id,
            'Content-Type': 'application/json'
          },
          // Axios'un otomatik stringify'ını önle
          transformRequest: [(data) => {
            console.log('🔥 SpreadsheetBuilder - PUT Transform request data parentId:', data.parentId);
            console.log('🔥 SpreadsheetBuilder - PUT Transform request data fileType:', data.fileType);
            return JSON.stringify(data);
          }]
        });
      } else {
        // Yeni kayıt
        content.id = 0;
        console.log('Yeni tablo oluşturuluyor');
        response = await axios.post('https://app.hhoms.com/api/form/Libraries', content, {
          headers: {
            ApplicationToken: appToken,
            User: userData.id,
            'Content-Type': 'application/json'
          },
          // Axios'un otomatik stringify'ını kontrol et
          transformRequest: [(data) => {
            console.log('🔥 SpreadsheetBuilder - POST Transform request data parentId:', data.parentId);
            console.log('🔥 SpreadsheetBuilder - POST Transform request data fileType:', data.fileType);
            return JSON.stringify(data);
          }]
        });
      }

      if (response.data) {
        message.success('Tablo başarıyla kaydedildi');
        setTableTitle(title);
        setTableDescription(description);
        
        // Yeni tabloysa Tables sayfasına yönlendir
        if (!formId) {
          navigate('/tables');
        }
        
        return true;
      }
    } catch (error) {
      console.error('Tablo kaydetme hatası:', error);
      if (error.message?.includes('parse')) {
        message.error('Tablo verisi formatında hata var');
      } else if (error.message?.includes('413')) {
        message.error('Tablo çok büyük, daha az veri ile tekrar deneyin');
      } else {
        message.error('Tablo kaydedilirken bir hata oluştu');
      }
      return false;
    }
  };

  // URL'den formId varsa tabloyu yükle
  useEffect(() => {
    if (initialData && initialData.formContent) {
      // SpreadsheetView'den gelen veriyi kullan - API çağrısı YOK
      console.log('SpreadsheetBuilder: initialData mevcut, direkt store\'a yükleniyor');
      console.log('InitialData:', {
        title: initialData.title,
        formContentType: typeof initialData.formContent,
        formContentLength: initialData.formContent?.length
      });
      
      // Store'a direkt veriyi yükle - API çağrısı yapmadan
      try {
        // formContent'i parse et
        let parsedContent;
        if (typeof initialData.formContent === 'string') {
          parsedContent = JSON.parse(initialData.formContent);
        } else {
          parsedContent = initialData.formContent;
        }
        
        // Sheets'i kontrol et ve yükle
        if (parsedContent.sheets && Array.isArray(parsedContent.sheets) && parsedContent.sheets.length > 0) {
          setSheets(parsedContent.sheets);
          setTableTitle(initialData.title || '');
          setTableDescription(initialData.description || '');
          console.log('SpreadsheetBuilder: initialData direkt yüklendi - API çağrısı yapılmadı');
        } else {
          console.warn('SpreadsheetBuilder: initialData sheets bulunamadı, boş tablo oluşturuluyor');
          // Boş tablo oluştur
          const createEmptyGrid = (rows = 100, cols = 100) => {
            const grid = [];
            for (let i = 0; i < rows; i++) {
              const row = [];
              for (let j = 0; j < cols; j++) {
                row.push({
                  value: '',
                  formula: '',
                  format: {
                    bold: false,
                    italic: false,
                    underline: false,
                    fontSize: 11,
                    fontFamily: 'Arial',
                    textColor: '#000000',
                    backgroundColor: '#ffffff',
                    horizontalAlign: 'left',
                    verticalAlign: 'top',
                    border: {
                      top: { style: 'none', color: '#000000' },
                      right: { style: 'none', color: '#000000' },
                      bottom: { style: 'none', color: '#000000' },
                      left: { style: 'none', color: '#000000' }
                    }
                  }
                });
              }
              grid.push(row);
            }
            return grid;
          };
          
          const emptySheet = {
            id: 'sheet_1',
            name: 'Sayfa 1',
            grid: createEmptyGrid(),
            activeCell: { row: 0, col: 0 },
            selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
            viewport: { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
            columnWidths: {},
            rowHeights: {}
          };
          setSheets([emptySheet]);
          setTableTitle(initialData.title || '');
          setTableDescription(initialData.description || '');
        }
      } catch (error) {
        console.error('SpreadsheetBuilder: initialData parse hatası:', error);
        // Parse hatası durumunda boş tablo oluştur
        const createEmptyGrid = (rows = 100, cols = 100) => {
          const grid = [];
          for (let i = 0; i < rows; i++) {
            const row = [];
            for (let j = 0; j < cols; j++) {
              row.push({
                value: '',
                formula: '',
                format: {
                  bold: false,
                  italic: false,
                  underline: false,
                  fontSize: 11,
                  fontFamily: 'Arial',
                  textColor: '#000000',
                  backgroundColor: '#ffffff',
                  horizontalAlign: 'left',
                  verticalAlign: 'top',
                  border: {
                    top: { style: 'none', color: '#000000' },
                    right: { style: 'none', color: '#000000' },
                    bottom: { style: 'none', color: '#000000' },
                    left: { style: 'none', color: '#000000' }
                  }
                }
              });
            }
            grid.push(row);
          }
          return grid;
        };
        
        const emptySheet = {
          id: 'sheet_1',
          name: 'Sayfa 1',
          grid: createEmptyGrid(),
          activeCell: { row: 0, col: 0 },
          selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
          viewport: { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
          columnWidths: {},
          rowHeights: {}
        };
        setSheets([emptySheet]);
        setTableTitle(initialData.title || '');
        setTableDescription(initialData.description || '');
      }
      
    } else if (formId) {
      console.log('SpreadsheetBuilder: formId mevcut, API çağrısı yapılıyor:', formId);
      loadSpreadsheetFromAPI(formId).then(() => {
        console.log('SpreadsheetBuilder: API çağrısı tamamlandı');
      }).catch((error) => {
        console.error('SpreadsheetBuilder: API çağrısı başarısız:', error);
      });
    }
  }, [formId, loadSpreadsheetFromAPI, initialData, setSheets, setTableTitle, setTableDescription]);





  // Kısayol tuşları
  useHotkeys('ctrl+c', (e) => {
    e.preventDefault();
    copySelection();
  }, { enableOnFormTags: true });

  useHotkeys('ctrl+v', (e) => {
    e.preventDefault();
    const { activeCell } = activeSheet;
    pasteSelection(activeCell.row, activeCell.col);
  }, { enableOnFormTags: true });

  useHotkeys('ctrl+z', (e) => {
    e.preventDefault();
    // TODO: Undo implementation
  }, { enableOnFormTags: true });

  useHotkeys('ctrl+y', (e) => {
    e.preventDefault();
    // TODO: Redo implementation
  }, { enableOnFormTags: true });

  useHotkeys('f2', (e) => {
    e.preventDefault();
    setEditing(true);
  }, { enableOnFormTags: true });

  useHotkeys('enter', (e) => {
    if (!isEditing) {
      e.preventDefault();
      const { activeCell } = activeSheet;
      setActiveCell(activeCell.row + 1, activeCell.col);
    }
  }, { enableOnFormTags: true });

  useHotkeys('tab', (e) => {
    if (!isEditing) {
      e.preventDefault();
      const { activeCell } = activeSheet;
      setActiveCell(activeCell.row, activeCell.col + 1);
    }
  }, { enableOnFormTags: true });

  useHotkeys('shift+tab', (e) => {
    if (!isEditing) {
      e.preventDefault();
      const { activeCell } = activeSheet;
      setActiveCell(activeCell.row, activeCell.col - 1);
    }
  }, { enableOnFormTags: true });

  useHotkeys('shift+enter', (e) => {
    if (!isEditing) {
      e.preventDefault();
      const { activeCell } = activeSheet;
      setActiveCell(activeCell.row - 1, activeCell.col);
    }
  }, { enableOnFormTags: true });

  // Arrow keys
  useHotkeys('up', (e) => {
    if (!isEditing) {
      e.preventDefault();
      const { activeCell } = activeSheet;
      setActiveCell(Math.max(0, activeCell.row - 1), activeCell.col);
    }
  }, { enableOnFormTags: true });

  useHotkeys('down', (e) => {
    if (!isEditing) {
      e.preventDefault();
      const { activeCell } = activeSheet;
      setActiveCell(activeCell.row + 1, activeCell.col);
    }
  }, { enableOnFormTags: true });

  useHotkeys('left', (e) => {
    if (!isEditing) {
      e.preventDefault();
      const { activeCell } = activeSheet;
      setActiveCell(activeCell.row, Math.max(0, activeCell.col - 1));
    }
  }, { enableOnFormTags: true });

  useHotkeys('right', (e) => {
    if (!isEditing) {
      e.preventDefault();
      const { activeCell } = activeSheet;
      setActiveCell(activeCell.row, activeCell.col + 1);
    }
  }, { enableOnFormTags: true });

  // Delete key
  useHotkeys('delete', (e) => {
    if (!isEditing) {
      e.preventDefault();
      const { activeCell } = activeSheet;
      // TODO: Clear cell content
    }
  }, { enableOnFormTags: true });

  // Escape key
  useHotkeys('escape', (e) => {
    if (isEditing) {
      e.preventDefault();
      setEditing(false);
    }
  }, { enableOnFormTags: true });



  return (
    <div className="spreadsheet-builder">
      {isLoading && (
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          backgroundColor: 'rgba(255, 255, 255, 0.8)', 
          zIndex: 1000 
        }}>
          <div className="loading-spinner" style={{ marginBottom: '16px' }}>⏳</div>
          <div style={{ fontSize: '14px', color: '#666' }}>Tablo yükleniyor...</div>
          <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>Büyük dosyalar biraz zaman alabilir</div>
        </div>
      )}

      <div className="spreadsheet-header">
        <SpreadsheetToolbar readOnly={isReadOnlyMode} onSave={handleSaveTable} />
        <FormulaBar readOnly={isReadOnlyMode} />
      </div>
      

      
      <div className="spreadsheet-content">
        <SheetTabs readOnly={isReadOnlyMode} />
        <div className="spreadsheet-grid-container" ref={gridRef}>
          <SpreadsheetGrid readOnly={isReadOnlyMode} />
        </div>
      </div>
      
      <ContextMenu readOnly={isReadOnlyMode} />
    </div>
  );
};

export default SpreadsheetBuilder;
